<template>
  <CustomTable ref="table" class="business-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:header-options>
      <div v-if="subFilters" class="sub-filters">
        <button v-for="(subFilter, index) in subFilters" :key="subFilter.label" class="sub-filter-button" :class="{ active: subFilterIndex === index }" @click="setSubFilter(index)">{{ subFilter.label }}</button>
      </div>
      <button @click="$refs.addBusinessModal.openModal()" class="action-button" type="button"><span class="fas fa-plus"></span>Nouveau devis</button>
    </template>
    <template v-slot:table>
      <thead>
      <tr>
        <custom-header field="number" class="hide-for-mobile col-fit">Devis</custom-header>
        <custom-header field="documents" class="hide-for-mobile col-fit">Documents</custom-header>
        <custom-header field="title" class="hide-for-mobile col-fill">Titre</custom-header>
        <custom-header field="status" class="hide-for-mobile col-fit">Status</custom-header>
        <custom-header class="col-fit" field="clientName">Client</custom-header>
        <custom-header field="ttcAmount" class="hide-for-mobile col-fit">Montant</custom-header>
        <custom-header class="col-fit" field="remaining">Solde</custom-header>
      </tr>
      </thead>
      <tbody v-if="!isLoading && data && data.length > 0">
      <tr v-for="(business, index) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="index" :class="{hide : isHiddenBusiness(business), 'business-row': !isHiddenBusiness(business)}" @click="openBusiness(business)">
        <template v-if="!refusedOrAbortedBusinessVisibility && (business.status === 'ABORTED' || business.status === 'REFUSED')">
          <td colspan="8" class="hide-for-mobile">Devis annulé ou refusé <a @click="this.refusedOrAbortedBusinessVisibility = !this.refusedOrAbortedBusinessVisibility">(afficher)</a></td>
        </template>
        <template v-else>
          <custom-body field="number" class="hide-for-mobile col-fit">
            <QuoteNumber :number="business.number" :order-number="haveProductionOption ? business.orderNumber : undefined" />
          </custom-body>
          <custom-body class="col-fit" field="documents">
            <DocumentChips :invoice-number="business.invoiceNumber" :deposit-number="business.depositNumber" :credit-note-numbers="(business.creditNotes || []).map(creditNote => creditNote.number)" />
          </custom-body>
          <custom-body field="title" class="hide-for-mobile col-fill" :title="business.title">{{ business.title ? business.title : ''}}</custom-body>
          <custom-body field="status" class="hide-for-mobile col-fit">
            <div class="status-event-container">
              <div v-if="isWaitingDeposit(business) || isWaitingSignature(business)"  class="tooltip-container">
                <font-awesome-icon class="tooltip-icon deposit-icon" :icon="isWaitingSignature(business) ? 'clock' : 'circle-info'" />
                <div class="tooltip-message">
                  <span v-if="isWaitingSignature(business)">En attente de la signature.</span>
                  <span v-if="isWaitingDeposit(business)">En attente de {{business.depositRemaining}}€ d'acompte.</span>
                </div>
              </div>
              <StatusBadge :status="business.status" />
            </div>
          </custom-body>
          <custom-body class="col-fit cell-strong col-truncate" field="clientName" :title="business.currentClientName ? business.currentClientName : (business.clientName ? business.clientName : '')">{{ business.currentClientName ? business.currentClientName : (business.clientName ? business.clientName : '')}}</custom-body>
          <custom-body field="ttcAmount" class="hide-for-mobile col-fit cell-strong">{{ business.ttcAmount ? business.ttcAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : ''}}</custom-body>
          <custom-body class="col-fit" field="remaining">{{business.remaining && business.invoiceNumber ? business.remaining.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })  + ' €' : ''}}</custom-body>
        </template>
      </tr>
      </tbody>
      <tbody v-else><tr class="empty-row">
        <td colspan="8" class="hide-for-mobile">Aucun contrat enregistré</td>
      </tr></tbody>
    </template>
  </CustomTable>
  <BusinessManagementModal ref="businessManagementModal" :callback=loadData />
  <AddBusinessModal ref="addBusinessModal" id="add-quote" :callback=openCreatedBusiness />
</template>

<script>
import AddBusinessModal from "@/components/modals/AddBusinessModal.vue";
import {BusinessController} from "@/controllers/business/BusinessController";
import BusinessManagementModal from "@/components/modals/BusinessManagementModal.vue";
import {BUSINESS_STATUS_ENUM} from "@/models/businessStatusEnum";
import CustomTable from "@/components/CustomTable.vue";
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMoneyBillTransfer, faList, faChevronLeft, faChevronRight, faPlus, faFilePen, faFileLines, faFilePdf, faCircleInfo, faClock, faDownload } from "@fortawesome/free-solid-svg-icons";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import DocumentChips from "@/components/tables/commons/DocumentChips.vue";
import QuoteNumber from "@/components/tables/commons/QuoteNumber.vue";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {COUNTS_REFRESH_EVENT} from "@/components/businessTabs";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {CustomTableService} from "@/services/CustomTableService";
library.add(faChevronLeft, faPlus, faChevronRight, faMoneyBillTransfer, faList, faFilePen, faFileLines, faFilePdf, faCircleInfo, faClock, faDownload);

export default {
  name: 'BusinessTable',
  // openedDocument : document sur lequel s'ouvre la modale du contrat ({type} d'un BUSINESS_DOCUMENT_TYPE_ENUM), le devis par défaut
  props: ['defaultFilters', 'subFilters', 'openedDocument'],
  computed: {
    BUSINESS_STATUS_ENUM() {
      return BUSINESS_STATUS_ENUM
    },
    haveProductionOption() {
      return localStorage && localStorage.options && localStorage.options.includes('production');
    }
  },
  components: {
    CustomBody,
    StatusBadge,
    DocumentChips,
    QuoteNumber,
    CustomHeader,
    CustomTable,
    BusinessManagementModal,
    AddBusinessModal,
  },
  data: function () {
    return {
      data: [],
      elements: [
        {label: "Échéance", value: "invoiceCreationDate", type: "date"},
        {label: "N°Devis", value: "number", type: "number"},
        {label: "N°Commande", value: "orderNumber", type: "number"},
        {label: "N°Facture", value: "invoiceNumber", type: "number"},
        {label: "Titre", value: "title", type: "text"},
        {label: "Status", value: "status", type: "select", values: BUSINESS_STATUS_ENUM},
        {label: "Client", value: "clientName", type: "text"},
        {label: "Montant", value: "ttcAmount", type: "number"},
        {label: "Solde", value: "remaining", type: "number"},
        {label: "Actions", value: undefined, type: undefined},
        {label: "Documents", value: "documents", type: undefined},
      ],
      searchOptions: {
        filters: [
          {field: "status", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [BUSINESS_STATUS_ENUM.REFUSED.value, BUSINESS_STATUS_ENUM.ABORTED.value]},
          {field: "status", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [BUSINESS_STATUS_ENUM.PAID.value, BUSINESS_STATUS_ENUM.PAID_AND_DECLARED.value]}
        ],
        sorts: [
          {field: "number", order: SORT_OPERATOR_ENUM.DESCENDING.value}
        ]
      },
      isLoading: true,
      hasLoadedOnce: false,
      refusedOrAbortedBusinessVisibility: false,
      currentChoice: undefined,
      subFilterIndex: -1
    }
  },
  beforeMount() {
    // Filtres par défaut d'un onglet : ils remplacent les filtres par défaut portant sur le même champ.
    if (this.defaultFilters && this.defaultFilters.length > 0) {
      const fields = this.defaultFilters.map(filter => filter.field);
      this.searchOptions = {
        ...this.searchOptions,
        filters: [
          ...this.searchOptions.filters.filter(filter => !fields.includes(filter.field)),
          ...this.defaultFilters
        ]
      };
    }
  },
  async mounted() {
    this.$refs.table.$refs.searchArray.initWithURLParams();

    // Lien vers un contrat précis (depuis un dossier) : seul le filtre sur son numéro s'applique, quel que soit son statut.
    const isOpeningBusiness = window.location.hash === '#openEditModal';

    // Initialisation uniquement : le premier sous-filtre est sélectionné par défaut, mais l'utilisateur peut ensuite le retirer.
    if (!isOpeningBusiness && this.subFilters && this.subFilters.length > 0) {
      this.applySubFilter(0);
    }

    if (isOpeningBusiness) {
      const filters = CustomTableService.data.get(this.$refs.table.tableId).currentFilters;
      filters.splice(0, filters.length, { field: "number", operator: FILTER_OPERATOR_ENUM.EQUAL.value, variable: this.$route.query.number });
      this.refusedOrAbortedBusinessVisibility = true;
    }

    await this.loadData();

    if(window.location.hash === "#openEditModal"){
      if(this.data.length === 1){
        this.openBusiness(this.data[0])
      }
    }

    if (window.location.hash === '#add-quote') {
      this.$refs.addBusinessModal.openModal();
      this.$refs.addBusinessModal.inputs.client.value = this.$route.query.clientId;
    }

    // Clean URL without reload
    const url = new URL(window.location.href)
    const baseUrl = url.origin + url.pathname
    window.history.replaceState({}, '', baseUrl)

    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    // Clic utilisateur : cliquer sur le sous-filtre actif le désélectionne (-1 = aucun sous-filtre = tous les résultats).
    setSubFilter(index) {
      this.applySubFilter(index === this.subFilterIndex ? -1 : index);
      this.loadData();
    },
    // Retire le sous-filtre appliqué puis pose celui d'index newIndex (-1 : aucun) dans les filtres du tableau,
    // modifiés sur place car c'est cette liste que lit buildURLParams() pour la requête.
    applySubFilter(newIndex) {
      const filters = CustomTableService.data.get(this.$refs.table.tableId).currentFilters;
      const isSameFilter = (a, b) => a.field === b.field && a.operator === b.operator && JSON.stringify(a.variable) === JSON.stringify(b.variable);
      const previous = this.subFilterIndex >= 0 ? this.subFilters[this.subFilterIndex].filter : undefined;
      const next = newIndex >= 0 ? this.subFilters[newIndex].filter : undefined;

      if (previous) {
        for (let i = filters.length - 1; i >= 0; i--) {
          if (isSameFilter(filters[i], previous)) {
            filters.splice(i, 1);
          }
        }
      }
      if (next) {
        filters.push({...next});
      }

      this.subFilterIndex = newIndex;
    },
    isWaitingDeposit(business) {
      const depositRemaining = business.orderId && business.deposit && business.depositRemaining;
      const quoteSent = business && [
        BUSINESS_STATUS_ENUM.SENT.value,
        BUSINESS_STATUS_ENUM.ACCEPTED.value,
        BUSINESS_STATUS_ENUM.IN_PROGRESS.value,
        BUSINESS_STATUS_ENUM.DONE.value
      ].includes(business.status);
      const isInvoice = !!business.invoiceNumber;
      return (quoteSent || isInvoice) && depositRemaining;
    },
    isWaitingSignature(business) {
      return business && business.status === BUSINESS_STATUS_ENUM.SENT.value && business.signature && !business.signature.signedAt;
    },
    async loadData(){
      this.$refs.table?.startLoading();
      this.isLoading = true;
      this.data = [];

      let resp = await BusinessController.getAll(this.$refs.table.buildURLParams());

      if(resp && !resp.errors){
        this.data.push(...resp);
      }

      this.isLoading = false;
      this.$refs.table?.stopLoading();
      // Les compteurs du menu ne sont recalculés que si les données ont changé (pas à l'ouverture de la rubrique)
      if (this.hasLoadedOnce) window.dispatchEvent(new Event(COUNTS_REFRESH_EVENT));
      this.hasLoadedOnce = true;
    },
    // Après création d'un devis : la modale du nouveau devis s'ouvre sans attendre le rechargement du tableau
    async openCreatedBusiness(businessId) {
      this.loadData();
      const business = await BusinessController.get(businessId);
      if (business && !business.errors) {
        this.$refs.businessManagementModal.openModal(business);
      }
    },
    isHiddenBusiness(business) {
      return !this.refusedOrAbortedBusinessVisibility && (business.status === 'ABORTED' || business.status === 'REFUSED');
    },
    // Le tableau ne reçoit que les champs affichés : la modale a besoin du business complet
    async openBusiness(business) {
      if (this.isHiddenBusiness(business) || !business.id) return;
      const fullBusiness = await BusinessController.get(business.id);
      if (fullBusiness && !fullBusiness.errors) {
        this.$refs.businessManagementModal.openModal(fullBusiness, this.openedDocument ? {type: this.openedDocument, creditNoteNumber: null} : undefined);
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';
.alert
  color #dc0000
  font-weight bold
.classic
  color auto
.warning
  color #d58900
  font-weight bold

.choice
  display none
  flex-direction column
  position absolute
  right 165px
  margin-top -50px
  padding 5px
  border-radius 6px
  background-color #b9cacf6e
  button
    height 35px
    padding 5px
    padding-right 20px
    padding-left 20px
    margin 5px
    background-color THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE
    border none
    border-radius 3px
    font-weight bold
    cursor pointer
  &.choice-displayed
    display flex !important

.fa-file-pdf
  margin-top -2px
  height 27px !important
  width 27px !important

.button-icon
  margin-right 10px

.hide
  height 25px
  td
    text-align center
    a
      cursor pointer

.action-td
  overflow visible
  display flex
  align-items center
  height 30px

.tooltip-container
  width 30px
  margin-left 5px
  position relative

  .tooltip-icon
    width 25px
    height 25px
    color INFO
    &:hover + .tooltip-message
      visibility visible
      opacity 1

  .tooltip-message
    display flex
    flex-direction column
    background-color white
    position absolute
    width 260px
    visibility hidden
    opacity 0
    top -6px
    left -280px
    z-index 10
    box-shadow 0 0 5px 0 rgba(0,0,0,.3)
    border-radius 5px
    padding 5px
    transition visibility 0s, opacity .3s linear

  .tooltip-message:hover
    visibility visible
    opacity 1

.status-event-container
  display flex
  align-items center
</style>